/**
 * Task filtering and sorting utilities (extracted from store for use with XState)
 */
import type { Task, TaskFilters, TaskSort } from './types'

export function getFilteredTasks(tasks: Task[], filters: TaskFilters): Task[] {
  return tasks.filter((task) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      const matchesSearch =
        task.title.toLowerCase().includes(searchLower) ||
        (task.description?.toLowerCase().includes(searchLower) ?? false) ||
        (task.assignedToName?.toLowerCase().includes(searchLower) ?? false) ||
        (task.customerName?.toLowerCase().includes(searchLower) ?? false)
      if (!matchesSearch) return false
    }
    if (filters.type) {
      const types = Array.isArray(filters.type) ? filters.type : [filters.type]
      if (!types.includes(task.type)) return false
    }
    if (filters.priority) {
      const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority]
      if (!priorities.includes(task.priority)) return false
    }
    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status]
      if (!statuses.includes(task.status)) return false
    }
    if (filters.assignedTo) {
      const assignees = Array.isArray(filters.assignedTo)
        ? filters.assignedTo
        : [filters.assignedTo]
      if (!assignees.includes(task.assignedTo)) return false
    }
    if (filters.customerId && task.customerId !== filters.customerId) return false
    if (filters.dueBefore) {
      if (new Date(task.dueDate) > new Date(filters.dueBefore)) return false
    }
    if (filters.dueAfter) {
      if (new Date(task.dueDate) < new Date(filters.dueAfter)) return false
    }
    if (filters.tags && filters.tags.length > 0) {
      const hasTags = filters.tags.some((tag) => task.tags?.includes(tag))
      if (!hasTags) return false
    }
    if (filters.columnId && task.columnId !== filters.columnId) return false
    if (filters.swimlaneId && task.swimlaneId !== filters.swimlaneId) return false
    if (filters.hasSubtasks !== undefined) {
      const hasSubtasks = (task.subtasks?.length ?? 0) > 0
      if (hasSubtasks !== filters.hasSubtasks) return false
    }
    if (filters.hasAttachments !== undefined) {
      const hasAttachments = (task.attachments?.length ?? 0) > 0
      if (hasAttachments !== filters.hasAttachments) return false
    }
    return true
  })
}

export function getSortedTasks(tasks: Task[], sort: TaskSort): Task[] {
  const priorityMap: Record<string, number> = { high: 3, medium: 2, low: 1 }
  return [...tasks].sort((a, b) => {
    let aValue: string | number
    let bValue: string | number
    switch (sort.by) {
      case 'title':
        aValue = a.title.toLowerCase()
        bValue = b.title.toLowerCase()
        break
      case 'dueDate':
        aValue = new Date(a.dueDate).getTime()
        bValue = new Date(b.dueDate).getTime()
        break
      case 'createdAt':
        aValue = new Date(a.createdAt).getTime()
        bValue = new Date(b.createdAt).getTime()
        break
      case 'updatedAt':
        aValue = new Date(a.updatedAt || a.createdAt).getTime()
        bValue = new Date(b.updatedAt || b.createdAt).getTime()
        break
      case 'priority':
        aValue = priorityMap[a.priority] ?? 0
        bValue = priorityMap[b.priority] ?? 0
        break
      case 'assignedTo':
        aValue = (a.assignedToName || a.assignedTo).toLowerCase()
        bValue = (b.assignedToName || b.assignedTo).toLowerCase()
        break
      default:
        return 0
    }
    if (aValue < bValue) return sort.order === 'asc' ? -1 : 1
    if (aValue > bValue) return sort.order === 'asc' ? 1 : -1
    return 0
  })
}
